import * as React from 'react';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../../redux/selectors/runtime/theme';
import {CustomScrollView} from '../../../shared/components/scrollView';
import {useStyles} from '../../../shared/hooks/styles';
import {VisImpStatus} from '../../../shared/models/visuallyImpairedSettings';
import {useTheme} from '../../../shared/theme';
import KeyboardHandleWrapper from '../../../shared/components/keyboardHandleWrapper';
import {ImageBackground, Platform, StyleProp, StyleSheet, TextStyle, View, ViewStyle} from 'react-native';
import {GradientView} from '../../../shared/components/gradientView';
import {LoginBg} from '../../../assets/images';
import {Logo} from './logo';
import {hexToRgbA} from '../../../shared/pipes';
import {LoginHeader} from './header';
import {DeviceSize} from '../../../shared/theme/helpers';
import {Back} from './withButtons';
import {Title} from './title';
import {LoginStepSelector} from '../../../redux/selectors/runtime/loginStep';
import {LoginState} from '../../../shared/models/login/loginStep';
import {useIsSmallScreenDevice} from '../../../shared/hooks/useIsSmallScreenDevice';

interface LoginLayoutProps {
  children: React.ReactNode;
  useBackButton?: boolean;
  title: string;
  topImage?: React.ReactNode;
  showHelpText?: boolean;
  showLogo?: boolean;
  showTitle?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  onBack?: () => void;
}

export const LoginLayout: React.FC<LoginLayoutProps> = ({
  children,
  title,
  useBackButton,
  topImage,
  showHelpText,
  showLogo = true,
  showTitle = true,
  containerStyle,
  titleStyle,
  onBack,
}) => {
  const theme = useTheme();
  const themeSettings = useSelector(themeSelector());
  const loginState = useSelector(LoginStepSelector());

  const isSmallScreen = useIsSmallScreenDevice();
  const styles = useStyles(({mediaOnly, platformOnly, align, deviceHeight}) => ({
    scrollViewContainer: {
      flex: 1,
      minHeight: 600,
    },
    background: fl([
      {
        display: 'flex',
        alignItems: 'center',
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
      platformOnly('web', {height: '100%'}, {height: '100%'}),
    ]),
    bgImage: {
      width: '100%',
      height: '100%',
      backgroundColor: theme.color.shaddowBg,
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    },
    contentWrapper: {
      flex: 1,
      alignItems: 'center',
      justifyContent: deviceHeight > 600 ? 'center' : 'flex-start',
      ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 40}, {paddingHorizontal: align(20)}),
      paddingBottom: isSmallScreen ? '0%' : 50,
      paddingTop: isSmallScreen ? 5 : 20,
    },
    container: fl([
      {display: 'flex', alignItems: 'center'},
      {
        flexShrink: 1,
        backgroundColor: theme.color.primaryNonGreyBackground,
        position: 'relative',
        borderRadius: 5,
        paddingBottom: 40,
        ...(isSmallScreen && {height: '85%'}),
        ...(Platform.OS === 'ios' && {height: 'auto'}),
      },
      mediaOnly(
        DeviceSize.desktop,
        {justifyContent: 'flex-start', paddingHorizontal: 40},
        {justifyContent: 'flex-start'},
      ),
      mediaOnly(
        DeviceSize.desktop,
        {width: 454, paddingTop: 30, minHeight: 505},
        {
          width: loginState === LoginState.esiaRegCardDeliveryHandling ? '100%' : '99%',
          paddingTop: 20,
          minHeight: loginState === LoginState.esiaRegCardDeliveryHandling ? (isSmallScreen ? '50%' : '94%') : 480,
        },
      ),
    ]),
    logo: {
      ...mediaOnly(DeviceSize.desktop, {}, {marginBottom: 15}),
    },
  }));
  const isVisuallyImpaired = themeSettings.visuallyImpaired === VisImpStatus.Active;

  return (
    <CustomScrollView scrollEnabled={false} withRefresh={false} contentContainerStyle={styles.scrollViewContainer}>
      <KeyboardHandleWrapper style={{flex: 1}} gapCorrection={Platform.OS === 'ios' ? 100 : undefined}>
        <ImageBackground source={isVisuallyImpaired ? null : (LoginBg as any)} style={styles.bgImage}>
          <GradientView
            style={styles.background}
            gradient={{angle: 90, colors: theme.screen.bgGradient.map((color) => hexToRgbA(color, 0.9))}}
          />
        </ImageBackground>
        <LoginHeader />
        <View style={styles.contentWrapper}>
          <View style={[styles.container, containerStyle]}>
            {showLogo ? <View style={styles.logo}>{topImage || <Logo />}</View> : null}
            {showTitle ? <Title title={title} showHelpText={showHelpText} titleStyle={titleStyle} /> : null}
            {children}
          </View>
          {useBackButton || onBack ? <Back onBack={onBack} /> : null}
        </View>
      </KeyboardHandleWrapper>
    </CustomScrollView>
  );
};

const fl = StyleSheet.flatten;
